import React, { useState, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FaTrash, FaStar, FaPlus } from 'react-icons/fa';
import '../../styles/ManageImages.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const ManageImages = ({
  show = false,
  onHide = () => {},
  styleId = null,
  currentImages = '',
  onSaveImages = () => {},
  BASE_URL: customBaseUrl
}) => {
  const baseUrl = customBaseUrl || BASE_URL;
  
  // Primary image from styles table
  const [primaryImage, setPrimaryImage] = useState('');
  // Additional images from images table
  const [additionalImages, setAdditionalImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  const fetchAdditionalImages = async () => {
    try {
      const response = await fetch(`${baseUrl}/api/admin/styles/styles/${styleId}`);
      const data = await response.json();
      
      if (data.success && data.additionalImages && Array.isArray(data.additionalImages)) {
        // Convert additional images to the format we use in state
        const formattedImages = data.additionalImages.map(img => ({
          name: img.image_path,
          isNew: false,
          isExisting: true,
          id: img.image_id
        }));
        setAdditionalImages(formattedImages);
      } else {
        setAdditionalImages([]);
      }
    } catch (err) {
      console.error('Error fetching additional images:', err);
      // Don't show error to user, just start with empty additional images
      setAdditionalImages([]);
    }
  };

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (show && currentImages) {
      // currentImages contains only primary image from styles table
      setPrimaryImage(currentImages.trim());
      setError('');
      setSuccess('');
      // Fetch additional images
      fetchAdditionalImages();
    } else if (show) {
      setPrimaryImage('');
      setAdditionalImages([]);
      setError('');
      setSuccess('');
    }
  }, [show, currentImages, styleId]);

  const handleAddImage = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setError('');

    const MAX_IMAGES = 4;
    const currentCount = additionalImages.length;
    const availableSlots = MAX_IMAGES - currentCount;

    if (availableSlots <= 0) {
      setError('Maximum of 4 additional images allowed');
      return;
    }

    if (files.length > availableSlots) {
      setError(`Only ${availableSlots} more image(s) can be added. Maximum is ${MAX_IMAGES} additional images.`);
      return;
    }

    const filesToAdd = [];
    let loadedCount = 0;

    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        setError('Only image files are allowed');
        return;
      }

      // For preview, create a URL for the file
      const reader = new FileReader();
      reader.onload = (event) => {
        filesToAdd.push({
          name: file.name,
          preview: event.target.result,
          isNew: true,
          file: file
        });
        
        loadedCount++;
        if (loadedCount === files.length) {
          setAdditionalImages(prev => [...prev, ...filesToAdd]);
        }
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = [...additionalImages];
    newImages.splice(index, 1);
    setAdditionalImages(newImages);
  };

  const handleAddImageClick = () => {
    if (fileInputRef.current && additionalImages.length < 4) {
      fileInputRef.current.click();
    }
  };

  const handleSave = async () => {
    if (!primaryImage) {
      setError('At least one primary image is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('styleId', styleId);
      formData.append('primaryImage', primaryImage);

      // Collect new additional image files
      const newImages = additionalImages.filter(img => img.isNew);
      newImages.forEach((img) => {
        formData.append('images', img.file);
      });

      // Collect existing additional images that are being kept
      const existingAdditional = additionalImages
        .filter(img => !img.isNew)
        .map(img => img.name);
      
      formData.append('existingAdditionalImages', JSON.stringify(existingAdditional));

      const response = await fetch(`${baseUrl}/api/admin/styles/manage-images`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Images updated successfully');
        // Return the primary image
        if (data.primaryImage) {
          onSaveImages(data.primaryImage);
        }
        setTimeout(() => onHide(), 1500);
      } else {
        setError(data.message || 'Failed to update images');
      }
    } catch (err) {
      console.error('Error updating images:', err);
      setError(err.message || 'Server error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="manage-images-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Manage Additional Images</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <div
            className="alert alert-danger alert-dismissible fade show"
            role="alert"
          >
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError("")}
              aria-label="Close"
            ></button>
          </div>
        )}
        {success && (
          <div
            className="alert alert-success alert-dismissible fade show"
            role="alert"
          >
            {success}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccess("")}
              aria-label="Close"
            ></button>
          </div>
        )}

        <div className="manage-images-container">
          {/* Left Section: Additional Images Grid */}
          <div className="images-left-section">
            <div className="additional-images-section">
              <div className="images-grid">
                {additionalImages.map((image, index) => {
                  const imageSrc =
                    image.preview || `${baseUrl}/uploads/styles/${image.name}`;

                  return (
                    <div key={index} className="image-card">
                      <div className="image-preview-container">
                        <img
                          src={imageSrc}
                          alt={`Additional ${index + 1}`}
                          className="image-preview"
                          onError={(e) => {
                            console.error("Image failed to load:", imageSrc);
                            e.target.src =
                              'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999" font-size="14"%3EError%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn-remove-image-card"
                        onClick={() => handleRemoveImage(index)}
                        disabled={loading}
                        title="Remove image"
                      >
                        <FaTrash size={12} />
                      </button>
                    </div>
                  );
                })}

                {/* Add Image Button */}
                <button
                  type="button"
                  className="image-card add-image-card"
                  onClick={handleAddImageClick}
                  disabled={loading || additionalImages.length >= 4}
                  title={additionalImages.length >= 4 ? "Maximum 4 images allowed" : "Add image"}
                >
                  <div className="image-preview-container add-icon">
                    <FaPlus size={32} />
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="section-divider"></div>

          {/* Right Section: Primary Image */}
          <div className="images-right-section">
            <h5 className="primary-title">Primary Image</h5>
            {primaryImage ? (
              <div className="primary-image-card">
                <div className="image-preview-container">
                  <img
                    src={`${baseUrl}/uploads/styles/${primaryImage}`}
                    alt="Primary"
                    className="image-preview"
                    onError={(e) => {
                      e.target.src =
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999" font-size="14"%3EError%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="no-primary-image">No primary image</div>
            )}
          </div>
        </div>

        {/* Hidden file input */}
        <Form.Control
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleAddImage}
          disabled={loading}
          style={{ display: 'none' }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={loading || !primaryImage}
        >
          {loading ? "Saving..." : "Save Images"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ManageImages;
